import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { MapPin, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const footerLinks = [
  { category: "Links", items: [
    { label: "About Us", href: "/about" },
    { label: "Careers", href: "/careers" },
    { label: "Portfolio", href: "/portfolio" },
    { label: "Investment Strategy", href: "#" },
    { label: "Insights", href: "/insights" },
    { label: "Contact Us", href: "#" },
  ]},
  { category: "Others", items: [
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Advertising and Cookies", href: "/advertising-cookies" },
    { label: "Terms of Use", href: "/terms-of-use" },
  ]},
];

const contactInfo = [
  { icon: MapPin, text: <>2 Eastbourne Terrace, Paddington,<br className="hidden md:inline" />{" "}London W2 6LG, UK</> },
  { icon: Mail, text: "info@nebula-f.com" },
];

export default function Footer() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <footer
      data-testid="footer"
      className="px-4 md:px-8 pt-0 md:pt-[75px] pb-[6px] md:pb-[22px]"
      ref={ref}
    >
      <motion.div
        initial={{ opacity: 0, y: 60, scale: 0.97 }}
        animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="relative rounded-[2.5rem] overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #08122a 0%, #091530 50%, #0b1a3a 100%)',
        }}
      >
        {/* Soft upper-middle radial glow for depth */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full blur-[120px]"
            style={{ background: 'radial-gradient(ellipse at center, rgba(79,107,255,0.06) 0%, rgba(91,140,255,0.03) 50%, transparent 75%)' }}
          />
        </div>

        {/* Top edge highlight */}
        <div className="absolute top-0 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-slate-500/20 to-transparent" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-8 md:px-14 pt-14 md:pt-16 pb-8">
          {/* Main grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 mb-12">
            {/* Links columns */}
            {footerLinks.map((section) => (
              <div key={section.category}>
                <h4 className="font-body text-xs font-semibold text-slate-400/80 mb-5 uppercase tracking-[0.15em]">
                  {section.category}
                </h4>
                <ul className="space-y-3">
                  {section.items.map((item) => (
                    <li key={item.label}>
                      <Link
                        to={item.href}
                        data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className="font-body text-[15px] text-slate-300/80 hover:text-white transition-colors duration-200"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Contact column */}
            <div>
              <h4 className="font-body text-xs font-semibold text-slate-400/80 mb-5 uppercase tracking-[0.15em]">
                Contact
              </h4>
              <ul className="space-y-4">
                {contactInfo.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <item.icon className="w-4 h-4 text-slate-500/70 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="font-body text-[15px] text-slate-300/80 leading-snug">
                      {item.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-slate-700/40 pt-6 mb-4">
            <p className="font-body text-sm text-slate-500/70">
              &copy; Copyright 2026 | Nebula Flow Solutions Inc.
            </p>
          </div>
        </div>

        {/* Large watermark text — embedded, sharp, pure white */}
        <div className="relative z-[1] overflow-hidden px-8 md:px-14 pb-6 -mt-2">
          <div
            data-testid="footer-watermark"
            className="font-heading font-bold text-center select-none pointer-events-none leading-[0.85] whitespace-nowrap"
            style={{
              fontSize: 'clamp(3rem, 13.5vw, 15rem)',
              color: 'rgba(255,255,255,0.06)',
              mixBlendMode: 'soft-light',
            }}
          >
            Nebula Flow
          </div>
        </div>
      </motion.div>
    </footer>
  );
}
